/**
 * Страница с ценами — /pricing
 *
 * Две витрины на одном движке. На сайте курсов (kurs.) страница только про курсы:
 * владелец 04.09.2026 — «здесь только курс, будь внимателен»: ни звёзд, ни викторин,
 * ни игр. На сайте игр — прежняя страница про игры и Telegram.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isVK, isCoursesSite } from '../platform/detect';
import { getCollections } from '../lib/collections-api';

type CourseRow = { id: string; name: string; icon?: string; access?: { type?: string; price?: number } };

function CoursesPricing() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseRow[]>([]);

  useEffect(() => {
    document.title = 'Цены на курсы — Курсы BESHEK';
    getCollections()
      .then((list: any) => setCourses((list?.collections ?? list ?? []) as CourseRow[]))
      .catch(() => setCourses([]));
  }, []);

  const paid = courses.filter((c) => c.access?.type === 'premium' || c.access?.type === 'freemium');
  const free = courses.filter((c) => !c.access?.type || c.access.type === 'free');

  return (
    <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D] p-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/collections')}
          className="px-3 py-1.5 bg-white border border-[#E1E5E3] hover:bg-white border border-[#E1E5E3] rounded-lg text-sm transition-all mb-6"
        >
          ← К курсам
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Цены на курсы</h1>
          <p className="text-[#0F766E]">Каждый курс — уроки, проверка после каждой темы и сертификат</p>
        </div>

        <div className="space-y-4">
          {/* Бесплатные */}
          <div className="bg-white border border-[#E1E5E3] rounded-2xl p-6 border border-[#E1E5E3]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Бесплатные курсы</h2>
                <p className="text-[#0F766E] text-sm">
                  {free.length ? `Сейчас открыто без оплаты: ${free.length}` : 'Открыты без оплаты'}
                </p>
              </div>
              <div className="text-3xl font-bold">0₽</div>
            </div>
            <ul className="space-y-2 text-[#626B73] text-sm mb-4">
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Все уроки курса</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Разбор на входе: с чего начать именно вам</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Проверка после каждой темы</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Сертификат о прохождении</li>
            </ul>
            <button
              onClick={() => navigate('/collections')}
              className="w-full py-3 bg-white border border-[#E1E5E3] hover:bg-white/30 rounded-xl font-medium transition-all"
            >
              Выбрать курс
            </button>
          </div>

          {/* Платные */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl p-6 border border-amber-400/50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Платные курсы</h2>
                <p className="text-[#0F766E] text-sm">Цена указана на карточке курса</p>
              </div>
            </div>
            {paid.length ? (
              <ul className="space-y-2 text-purple-100 text-sm mb-4">
                {paid.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2">
                    <button onClick={() => navigate(`/collections/${c.id}`)} className="text-left hover:underline">
                      {c.icon ? `${c.icon} ` : ''}{c.name}
                    </button>
                    <span className="font-bold whitespace-nowrap">
                      {c.access?.price ? `${c.access.price}₽` : '—'}
                      {c.access?.type === 'freemium' ? ' · начало бесплатно' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[#626B73] text-sm mb-4">
                Сейчас все курсы открыты бесплатно. Платные курсы появятся с ценой на карточке;
                у части из них первые разделы останутся бесплатными.
              </p>
            )}
            <ul className="space-y-2 text-[#626B73] text-sm">
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Бессрочный доступ после оплаты</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Оплата картой или СБП на сайте</li>
            </ul>
          </div>

          {/* Тарифы и условия — то, что смотрит модерация платёжного сервиса */}
          <div className="bg-white border border-[#E1E5E3] rounded-2xl p-6 border border-[#E1E5E3] text-sm text-[#626B73] space-y-3">
            <h2 className="text-xl font-bold text-[#252A2D]">Тарифы и условия</h2>
            <table className="w-full text-left">
              <tbody>
                <tr className="border-b border-[#E1E5E3]"><td className="py-2">Бесплатный курс</td><td className="py-2 text-right font-bold text-[#252A2D]">0 ₽</td></tr>
                <tr className="border-b border-[#E1E5E3]"><td className="py-2">Платный курс, полный доступ ко всем урокам</td><td className="py-2 text-right font-bold text-[#252A2D]">300 ₽</td></tr>
                <tr><td className="py-2">Условно-платный курс: первые разделы бесплатно, остальные</td><td className="py-2 text-right font-bold text-[#252A2D]">300 ₽</td></tr>
              </tbody>
            </table>
            <p>Точная цена конкретного курса указана на его карточке; при отличии от таблицы действует цена на карточке.</p>
            <p>Что входит: все уроки курса, проверка после каждой темы, разбор на входе, сертификат о прохождении. Доступ открывается сразу после оплаты и не ограничен по сроку.</p>
            <p>Оплата: банковской картой или через СБП на защищённой странице платёжного сервиса; наличные и переводы на карту не принимаются.</p>
            <p>Возврат: по <a href="/refund" className="text-[#0F766E] underline">правилам возврата</a>. Продавец и реквизиты: в <a href="/terms" className="text-[#0F766E] underline">условиях оказания услуг</a>.</p>
          </div>
        </div>

        <div className="mt-8 text-center space-y-2 text-sm">
          <a href="/terms" className="text-purple-400 hover:text-[#0F766E] block">Условия оказания услуг</a>
          <a href="/refund" className="text-purple-400 hover:text-[#0F766E] block">Правила возврата</a>
          <a href="/support" className="text-purple-400 hover:text-[#0F766E] block">Поддержка</a>
        </div>
      </div>
    </div>
  );
}

function GamesPricing() {
  const navigate = useNavigate();
  const vk = isVK();

  return (
    <div className="min-h-screen bg-[#F7F7F4] text-[#252A2D] p-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="px-3 py-1.5 bg-white border border-[#E1E5E3] hover:bg-white border border-[#E1E5E3] rounded-lg text-sm transition-all mb-6"
        >
          ← Меню
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">💎 Тарифы</h1>
          <p className="text-[#0F766E]">Выберите подходящий вариант</p>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#E1E5E3] rounded-2xl p-6 border border-[#E1E5E3]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Бесплатно</h2>
                <p className="text-[#0F766E] text-sm">Базовый доступ</p>
              </div>
              <div className="text-3xl font-bold">0₽</div>
            </div>
            <ul className="space-y-2 text-[#626B73] text-sm mb-4">
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>BrainSwipe — бесконечные викторины</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>Daily Challenge</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>AI-игры с друзьями</li>
              <li className="flex items-center gap-2"><span className="text-[#0F766E]">✓</span>XP, уровни, достижения</li>
            </ul>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 bg-white border border-[#E1E5E3] hover:bg-white/30 rounded-xl font-medium transition-all"
            >
              Начать бесплатно
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-purple-400 text-sm mb-3">Пакеты игр:</p>
          <p className="text-purple-500 text-xs mt-3">
            {vk ? 'Оплата голосами ВКонтакте' : 'В Telegram — звёздами, в боте'}
          </p>
        </div>

        <div className="mt-8 text-center space-y-2 text-sm">
          <a href="/terms" className="text-purple-400 hover:text-[#0F766E] block">Договор-оферта</a>
          <a href="/refund" className="text-purple-400 hover:text-[#0F766E] block">Политика возврата</a>
        </div>
      </div>
    </div>
  );
}

export function PricingPage() {
  return isCoursesSite() ? <CoursesPricing /> : <GamesPricing />;
}
